import { z } from "zod";
const featureFlagDefinitions = {
  AgentsTab: {
    value: "agents-tab",
    description: "Makes the Agents tab visible in the navigation header top bar"
  },
  AutomatedOutreach: {
    value: "automated-outreach",
    description: "When enabled, pending participants (created via CSV or the API) will automatically be sent an SMS invite to take their interview."
  },
  AutomationReportEmailContent: {
    value: "automation-report-email-content",
    description: "Embeds screened and redacted automation report content in notification emails for companies whose link-only report email setting is disabled; while off, their emails remain link-only."
  },
  InterviewSections: {
    value: "interview-sections",
    description: "Shows interview sections cards in the job type slideover and allows sections to be added. * "
  },
  KnowledgeBase: {
    value: "knowledge-base",
    description: `Enable the display of the "Knowledge Bases" section in the settings as well as the "Knowledge bases" dropdown in the playbook editor for teammate performance knowledge base adherence scoring. Note: "Knowledge bases" and KB adherence scoring is always available/enabled for voice agents.`
  },
  HideHireTab: {
    value: "hide-hire-tab",
    description: "Despite the name, hides the whole recruiting surface for a company, not just the Hire tab: also the recruiting-flavoured settings sections and the Interview resource types. The inverse of the legacy PostHog `recruiting` flag, which was on for everyone but an exclusion list \u2014 naming the exclusion directly means companies created later keep recruiting without anyone adding a row."
  },
  Scheduling: {
    value: "scheduling",
    description: ""
  },
  TeamPerformanceTab: {
    value: "team-performance-tab",
    description: "Display the Team performance tab in the top navigation bar."
  },
  Training: {
    value: "training",
    description: ""
  },
  LiveCoaching: {
    value: "live-coaching",
    description: ""
  },
  LiveCoachingPreview: {
    value: "live-coaching-preview",
    description: ""
  },
  Reports: {
    value: "reports",
    description: ""
  },
  PerformanceReport: {
    value: "performance-report",
    description: "Show performance report card on reports page"
  },
  InsuranceReports: {
    value: "insurance-reports",
    description: "Show insurance upsell reports on reports page"
  },
  ReportsResearchAgent: {
    value: "reports-research-agent",
    description: "Enables the Research Agent panel on the reports page (and hides the bottom-bar variant while it's visible)."
  },
  TopicsAndSignals: {
    value: "topics",
    description: "Enables topics and signals views to both accessable to the customer. These two features are released at the same time"
  },
  VocalIntelligence: {
    value: "vocal-intelligence",
    description: "When enabled, surfaces the vocal intelligence radar chart (authority/energy/rapport, scored 1-5) on the teammate performance overview."
  },
  CustomerExperienceScore: {
    value: "customer-experience-score",
    description: "When enabled, surfaces customer experience score values on the frontend \u2014 the per-grading 1-5 pill in the grading assessment view and gradings tables, and the per-teammate 0-100 average column in the team performance tables."
  },
  FunnelOutcome: {
    value: "funnel-outcome",
    description: "Exposes the Funnel outcome type in the playbook outcome type picker. Playbooks that already have a Funnel outcome saved continue to show it regardless of this flag."
  },
  GenesysIntegration: {
    value: "genesys-integration",
    description: "Shows the Genesys Cloud card in Settings \u2192 Integrations, letting an admin connect, pause, and disconnect the call-recording sync themselves. Off by default: only contact-centre customers on Genesys should see it."
  },
  ZoomCcIntegration: {
    value: "zoom-cc-integration",
    description: "Shows the Zoom Contact Center card in Settings \u2192 Integrations, letting an admin connect, pause, and disconnect the call-recording sync themselves. Off by default: only contact-centre customers on Zoom should see it."
  },
  MicrosoftTeams: {
    value: "microsoft-teams",
    description: "Shows the Microsoft Teams surfaces in the admin app: the Teams card in Settings \u2192 Integrations and the Microsoft Teams delivery channel option in agent notification settings."
  },
  OutboundVoiceAgents: {
    value: "outbound-voice-agents",
    description: "Lets a company schedule outbound calls for its voice agents through the public Call Task API (POST /api/v1/call-tasks and friends). Off means the endpoints are rejected as forbidden."
  },
  AgentManagedOutreach: {
    value: "agent-managed-outreach",
    description: "Shows the 'Agent-managed outreach' toggle in an agent's Outbound calls settings."
  },
  NewBilling: {
    value: "new-billing",
    description: "Replaces the Billing settings page with the redesigned plan-summary + AI credits usage page."
  },
  BasetenGrading: {
    value: "baseten-grading",
    description: "Grades newly enqueued teammate/account assessments (e.g. recordings ingested via the public API) with Baseten as the inference provider, routed through the dedicated Baseten grading queue and worker. HQ regrade tooling defaults its inference mode from this flag but can override it."
  },
  NearDuplicateRecordingCheck: {
    value: "near-duplicate-recording-check",
    description: "Fingerprints every recording a company uploads through the public API and its intake integrations, indexes it, and logs the closest existing recording (a near-duplicate check, in log-only mode). Nothing is deduplicated by this flag alone; turn it on and backfill the company's history before considering the dedup flag. Off stops every part of the feature for the company. NOT TESTED AT HIGH VOLUME: before the recording is created, the upload request awaits a fingerprint Lambda (deadline 45 s for audio, 180 s for video), a ClickHouse ranking query and a Postgres recent-band read, then serialises on a per-company advisory lock behind a four-permit in-process gate. Every stage degrades to creating the recording as it does today when it fails or times out, but the latency is added to every upload and the lock makes a company's concurrent uploads wait on each other, so enabling this for a high-volume customer risks recording upload API calls timing out."
  },
  NearDuplicateRecordingDedup: {
    value: "near-duplicate-recording-dedup",
    description: "Resolves an upload whose audio near-matches an existing recording of the same teammate to that recording instead of creating a new one, the way a byte-identical re-upload already does. Implies the check flag. Enable only after the check flag's log-only period shows the expected match rate for the company. Adds no work to the upload: the fingerprint, the queries and the per-company lock all belong to the check flag and are taken whenever it is on. What this changes is the outcome, so a false match resolves an upload to the wrong recording instead of merely logging it."
  },
  SplitCoachingPipeline: {
    value: "split-coaching-pipeline",
    description: "Generates weekly coaching with the split pipeline (a focus agent picks the week's goal and up to three further opportunities, then one note agent per criterion writes a note) instead of the single coaching agent. Rows keep the single agent's shape, with the goal note under `criteria`, plus `opportunities`. Off = original pipeline; safe to turn off again at any time."
  }
};
const featureFlagKeySchema = z.string().min(1).max(100).regex(
  /^[a-z0-9]+(-[a-z0-9]+)*$/,
  "Feature flag keys are lower-case kebab-case"
).brand();
function asFeatureFlagKey(key) {
  return featureFlagKeySchema.parse(key);
}
const archivedFeatureFlagValueDefinitions = {
  "agent-automations-demo": true,
  "agent-teammate-coaching": true,
  "customer-interaction-splitting": true,
  "customer-interactions-contact-name-filter": true,
  "display-sample-tool-health-check-results": true,
  "durable-outbound-calls": true,
  forms: true,
  "playbook-feedback-review-tab": true,
  "redact-ssn": true
};
const archivedFeatureFlagValues = new Map(
  Object.entries(archivedFeatureFlagValueDefinitions).map(([key, value]) => [
    asFeatureFlagKey(key),
    value
  ])
);
function getArchivedFeatureFlagValue(key) {
  return archivedFeatureFlagValues.get(key);
}
function isFeatureFlagArchived(key) {
  return archivedFeatureFlagValues.has(key);
}
const FeatureFlagKey = Object.fromEntries(
  Object.entries(featureFlagDefinitions).map(([name, { value }]) => [
    name,
    asFeatureFlagKey(value)
  ])
);
const featureFlagDescriptions = new Map(
  Object.values(featureFlagDefinitions).map((definition) => [
    asFeatureFlagKey(definition.value),
    definition.description
  ])
);
export {
  FeatureFlagKey,
  archivedFeatureFlagValues,
  asFeatureFlagKey,
  featureFlagDescriptions,
  featureFlagKeySchema,
  getArchivedFeatureFlagValue,
  isFeatureFlagArchived
};
//# sourceMappingURL=keys.js.map
